import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import e3dModel from "../../model/e3dModel";
import E3dListElement from "./E3dListElement";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import UserRoles from "../../model/enums/UserRole";
import ConditionReport from "../../model/ConditionReport";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

/*
  Component to manage the display of the existing SITES list
*/
function E3dList() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<e3dModel[]>([]);
  const [showMore, setShowMore] = useState(false);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<e3dModel[]>>({
        url: APIEndPoints.E3DS.PAGED(1, 5, parseInt(id!)),
        requiresToken: true,
      })
      .then(async (response: PagedData<e3dModel[]>) => {
        // get condition report information on each 3D
        for (let i = 0; i < response.items.length; i++) {
          const item = response.items[i];
          const condRep = await apiClient.get<PagedData<ConditionReport[]>>({
            url: APIEndPoints.CONDITION_REPORTS.ALL(item.id!),
          });
          if (
            condRep &&
            Array.isArray(condRep.items) &&
            condRep.items.length > 0
          ) {
            item.condition_report = condRep.items[0];
          }
        }
        setData(response.items.slice(0, 4));
        setShowMore(response.items.length > 4);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, id, navigate]);

  const handleShowMore = () => {
    apiClient
      ?.get<PagedData<e3dModel[]>>({
        url: APIEndPoints.E3DS.ALL(parseInt(id!)),
        requiresToken: true,
      })
      .then((response: PagedData<e3dModel[]>) => {
        setData(response.items);
        setShowMore(false);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-grid gap-16">
      {data?.length > 0 &&
        data?.map((item, index) => {
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);
          return (
            <E3dListElement
              data={item}
              key={item.id}
              canEdit={canEdit}
              index={index}
            />
          );
        })}

      {showMore && (
        <div className="details-show-more">
          <Button
            label="Show all e3D records"
            onClick={handleShowMore}
            purpose={ButtonAction.UPDATE}
          />
        </div>
      )}

      {data?.length === 0 && (
        <div className="details-no-records">No e3D records available.</div>
      )}
    </div>
  );
}

export default E3dList;
